/**
 * parseApiError — turn a non-2xx Response into a typed
 * { field, message } pair the page can render inline.
 *
 * Per SOP 02 §"Cross-cutting Utilities (LOCKED)": every viewmodel that
 * does a mutation MUST use this helper instead of hand-rolling its
 * own JSON parsing + field extraction. Previously duplicated across
 * ~12 viewmodel files; now one home, one test, one envelope contract.
 *
 * Envelope contract (mirror of `gateway/responses.ts`):
 *   { success: false, error: { code: string; message: string; field?: string | null } }
 *
 * Fallbacks (must NEVER leak status codes / URLs / "Failed to fetch"):
 *   - 5xx → generic SERVER_UNAVAILABLE_MESSAGE. The gateway / a worker
 *     is unreachable or crashed; the user can't act on the status code.
 *   - body isn't JSON / doesn't match the envelope (e.g. CF HTML error
 *     page) → same generic message.
 *   - 4xx with a valid envelope → relay the server-supplied message
 *     (that's a real validation / domain error the operator can act on).
 */

import { SERVER_UNAVAILABLE_MESSAGE } from './server-unavailable'

interface ApiErrorBody {
  readonly success: false
  readonly error: {
    readonly code: string
    readonly message: string
    readonly field?: string | null
  }
}

export interface ParsedApiError {
  readonly field: string | null
  readonly message: string
}

export async function parseApiError(res: Response): Promise<ParsedApiError> {
  // 5xx — always generic. Server isn't telling us anything actionable;
  // even if the body happens to be JSON it shouldn't surface raw.
  if (res.status >= 500) {
    return { field: null, message: SERVER_UNAVAILABLE_MESSAGE }
  }
  try {
    const body = (await res.json()) as ApiErrorBody
    if (typeof body?.error?.message === 'string' && body.error.message.length > 0) {
      return {
        field: body.error.field ?? null,
        message: body.error.message
      }
    }
    // Valid JSON but not our envelope shape — treat as opaque.
    return { field: null, message: SERVER_UNAVAILABLE_MESSAGE }
  } catch {
    // Non-JSON body (CF HTML error page, edge timeout, etc.) — same.
    return { field: null, message: SERVER_UNAVAILABLE_MESSAGE }
  }
}
